import { contactsOwnerContext } from '@modules/contacts/services/contacts-owner-context';
import { ChangeDetectorRef, Component, inject, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { Stitch } from '@modules/common/types/stitch';
import { FormChangesModalsService } from '@modules/common/services/form-changes-modals.service';
import { ContactsService } from '@modules/contacts/services/contacts.service';
import type { ContactSummaryDto } from '@sharedlib/dtos/dto/contacts-app/contact.dto';
import { Contact } from '@modules/contacts/types/contact';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { SendAsIdentitiesService } from '@modules/messages/services/send-as-identities.service';
import { EmailAddressFeaturesService } from '@modules/messages/services/email-address-features.service';
import { MessagesService } from '@modules/messages/services/messages.service';
import { MailAppSettingsService } from '@modules/messages/services/settings.service';
import { MAIL_SETTINGS_REFUSED_REASON, mayChangeBlockList } from '@modules/messages/utils/sender-actions';
import { EmailAddressFeature } from '@modules/messages/types/email-address-feature';
import {
  addressAddedToListText,
  addressRemovedFromListText,
  BLACK_LIST_LABEL,
} from '@modules/messages/utils/email-address-list-toasts';
import { Message } from '@modules/messages/types/message';
import { Participant } from '@modules/messages/types/participant';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { AccountService } from '@frontendlib/common/services/account.service';
import { fromEvent, Observable, Subject } from 'rxjs';
import { first, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-participant-context-menu',
  templateUrl: './participant-context-menu.component.html',
  styleUrls: ['./participant-context-menu.component.less'],
  standalone: false,
})
export class ParticipantContextMenuComponent implements OnInit, OnDestroy {
  private alive = new Subject<void>();
  private emailAddressFeatures: EmailAddressFeature[] = [];
  private senderContactIds: string[] = [];
  private senderContacts: ContactSummaryDto[] = [];
  protected isSenderVip = false;
  protected isBlackListed = false;
  protected isOwnAddress = false;
  protected ownAddressLoaded = false;
  /** The participant's own `contactIds` when the caller resolved them, otherwise this menu's lookup. */
  protected contactIds: string[] = [];
  // Until the lookup answers, "Add to Contacts" may be a guess for somebody who already has a contact.
  protected contactsLoaded = false;
  protected contactsReachable = true;
  // Until the lookup answers, the label is a guess: an "Add" click on a listed sender would duplicate its row.
  protected blackListLoaded = false;

  // Inputs
  @Input() participant: Participant;
  @Input() tenantItems: ReadonlyArray<{ readonly accountId?: string }> = [];

  /**
   * Controls visibility of contact options:
   * - "Add to Contacts" (for auto-generated contacts)
   * - "Open Contact" (for a single linked contact)
   * - "View Contacts" (for multiple linked contacts)
   */
  @Input() showContactOptions = false;

  // Services
  private readonly closePopoverToken = inject(CLOSE_POPOVER, { optional: true });
  private readonly messagesService = inject(MessagesService);
  private readonly contactService = inject(ContactsService);
  private readonly dpService = inject(DynamicPanelService);
  private readonly emailAddressFeaturesService = inject(EmailAddressFeaturesService);
  private readonly formChangesModalsService = inject(FormChangesModalsService);
  private readonly injector = inject(Injector);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly accountService = inject(AccountService);
  private readonly sendAsIdentities = inject(SendAsIdentitiesService, { optional: true });
  // Only the Mail space context provides it; elsewhere the entry stays usable and the server decides (#2412).
  private readonly mailSettings = inject(MailAppSettingsService, { optional: true });
  protected readonly MAIL_SETTINGS_REFUSED_REASON = MAIL_SETTINGS_REFUSED_REASON;

  /** The Black List entry writes the mailbox's block list, so it follows the mailbox settings authority (#2412). */
  protected get blockListReadOnly(): boolean {
    return !mayChangeBlockList(this.mailSettings);
  }

  /** Your own address is never offered "Add to Contacts"; an existing contact of yours still opens. */
  protected get showsContactItem(): boolean {
    return this.showContactOptions && this.contactsReachable && (this.contactIds.length > 0 || !this.isOwnAddress);
  }

  constructor() {}

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.ownAddresses()
      .pipe(takeUntil(this.alive))
      .subscribe((addresses) => {
        const address = this.participant.address?.trim().toLowerCase();
        this.isOwnAddress = !!address && addresses.some((own) => own.trim().toLowerCase() === address);
        this.ownAddressLoaded = true;
        this.changeDetectorRef.markForCheck();
      });

    this.emailAddressFeaturesService
      .search({ values: [this.participant.address] })
      .pipe(takeUntil(this.alive))
      .subscribe(({ items: participants }) => {
        this.emailAddressFeatures = participants;
        // Read here rather than off the message, which no longer carries it (SBUX-1090).
        this.isBlackListed = participants.some(
          ({ type, value }) => type === 'black-list' && value === this.participant.address,
        );
        this.blackListLoaded = true;
        this.changeDetectorRef.markForCheck();
      });

    if (this.participant.contactIds) {
      this.contactIds = this.participant.contactIds;
      this.contactsLoaded = true;
    }

    this.contactsReachable = !this.tenantItems.length || !!contactsOwnerContext(this.tenantItems);
    if (!this.contactsReachable) return;
    this.contactService
      .getContactsByEmails([this.participant.address], this.tenantItems.length ? { tenantItems: this.tenantItems } : undefined)
      .pipe(takeUntil(this.alive))
      .subscribe((contacts) => {
        this.senderContacts = contacts;
        this.senderContactIds = contacts.map((contact) => contact.id);
        this.isSenderVip = contacts.some((contact) => contact.vip);
        this.contactIds = this.participant.contactIds ?? this.senderContactIds;
        this.contactsLoaded = true;
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  protected openContact(contactUid: string): void {
    this.withUnsavedChangesCheck(() => {
      const contact = this.senderContacts.find(({ id }) => id === contactUid);
      const tenantItems = contact?.accountId ? [contact] : this.tenantItems;
      this.contactService
        .getItem(contact?.accountId ? contact : contactUid, tenantItems.length ? { tenantItems } : undefined)
        .pipe(take(1))
        .subscribe((item) => {
          this.dpService.setFormItem(item as Stitch);
        });
    });
  }

  /**
   * Opens Dynamic Panel with Contact Edit Form, with Save & Cancel buttons visible.
   *
   * `vip` pre-flags the form for the "Add to VIPs" flow: a contact must exist before it can be
   * a VIP, so the user completes it here and the flag is persisted on save.
   */
  protected addToContacts(participant: Participant, { vip = false }: { vip?: boolean } = {}): void {
    if (this.tenantItems.length && !contactsOwnerContext(this.tenantItems)) return;
    this.withUnsavedChangesCheck(() => {
      const contact = Contact.fromParticipant(participant, { vip });
      contact.accountId = this.tenantItems.find(({ accountId }) => accountId)?.accountId;
      this.dpService.setFormItem(contact);
    });
  }

  protected viewContacts(contactUIDs: string[]): void {
    if (!contactUIDs || contactUIDs.length === 0) {
      console.warn('No contact UIDs provided');
      return;
    }

    this.withUnsavedChangesCheck(() => {
      this.dpService.setFormItem(null);
      const contacts = this.senderContacts.filter(({ id }) => contactUIDs.includes(id));
      const tenantItems = contacts.some(({ accountId }) => accountId) ? contacts : this.tenantItems;
      this.dpService.openSearchByIds(StitchType.CONTACT, contactUIDs, tenantItems.length ? tenantItems : undefined);
    });
  }

  protected toggleVip(): void {
    if (!this.participant) {
      return;
    }

    if (this.senderContactIds.length === 0) {
      this.addToContacts(this.participant, { vip: true });
      return;
    }

    const vip = !this.isSenderVip;

    const tenantItems = this.senderContacts.some(({ accountId }) => accountId) ? this.senderContacts : this.tenantItems;
    const write = tenantItems.length
      ? this.contactService.vip({ ids: this.senderContactIds }, vip, { tenantItems })
      : this.contactService.vip({ ids: this.senderContactIds }, vip);
    write
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.isSenderVip = vip;
        this.changeDetectorRef.markForCheck();
        this.closePopoverToken?.next();
      });
  }

  // SBUX-824 moved VIP to `toggleVip` (the flag lives on the contact); SBUX-807 retired White List.
  // Black List is all that is left as a mail-side list this menu toggles.
  protected moveToList(type: 'black-list'): void {
    if (!this.participant || !this.blackListLoaded || this.blockListReadOnly) {
      return;
    }

    if (this.isBlackListed) {
      const permission = this.emailAddressFeatures.find(
        (permissionParticipant) =>
          permissionParticipant.type === type && this.participant.address === permissionParticipant.value,
      );

      !permission && this.closePopoverToken?.next();

      permission &&
        this.emailAddressFeaturesService
          .deletePermanently(permission.uid, {
            message: addressRemovedFromListText(permission.value, type, BLACK_LIST_LABEL),
          })
          .pipe(takeUntil(this.alive))
          .subscribe({
            next: () => {
              this.isBlackListed = false;
              this.closePopoverToken?.next();
            },
            // The service already told the person why; the flag stays as it was.
            error: () => this.closePopoverToken?.next(),
          });
    } else {
      this.emailAddressFeaturesService
        .create(
          new EmailAddressFeature({
            value: this.participant.address,
            type,
          }),
          { message: addressAddedToListText(this.participant.address, type, BLACK_LIST_LABEL) },
        )
        .pipe(takeUntil(this.alive))
        .subscribe({
          next: () => {
            this.isBlackListed = true;
            this.closePopoverToken?.next();
          },
          error: () => this.closePopoverToken?.next(),
        });
    }
  }

  protected copy(): void {
    fromEvent(document, 'copy')
      .pipe(take(1))
      .subscribe((clipboard: ClipboardEvent) => {
        clipboard.clipboardData.setData('text/plain', this.participant?.address);
        clipboard.preventDefault();
      });
    document.execCommand('copy');
  }

  protected compose(): void {
    this.messagesService.composeMessage({
      message: new Message({ to: [this.participant] }),
      injector: this.injector,
    });
  }

  /**
   * Helpers
   */

  private ownAddresses(): Observable<string[]> {
    return (
      this.sendAsIdentities?.ownAddresses() ??
      this.accountService.getAccount().pipe(map(({ email }) => (email ? [email] : [])))
    );
  }

  /**
   * Wraps the provided action with a check for unsaved changes. If there are unsaved changes,
   * the user is prompted with a confirmation dialog before the action is executed.
   *
   * @param {() => void} action The action to be executed if the user confirms they want to proceed despite unsaved changes.
   * @return {void} No return value.
   */
  private withUnsavedChangesCheck(action: () => void): void {
    this.formChangesModalsService
      .confirmUnsavedChanges()
      // it's safe to use first() here
      // and not takeUntil(this.alive) - in fact this will not work as this menu is closed right after click
      // and confirmUnsavedChanges is emitted only once anyway for our case
      .pipe(first())
      .subscribe((canLeave) => {
        if (canLeave) {
          action();
        }
      });

    this.closePopoverToken?.next();
  }
}
